import React from 'react';
import PropTypes from 'prop-types';
import { Link, Stack, Typography } from '@mui/material';
import {
  getValidSponsors,
  separateSponsorsByType,
} from '../utils/sponsorUtils';

const SponsorsName = ({
  sponsors,
  linkColor,
  underline,
  direction = 'column',
  maxDisplay = 3, // Maximum number of sponsors to display before truncating
  showTruncated = true, // Whether to show "+X more" when truncated
}) => {
  if (!sponsors || sponsors.length === 0) {
    return null;
  }

  const validSponsors = getValidSponsors(sponsors);

  if (validSponsors.length === 0) {
    return null;
  }

  // Separate curators from labels if needed
  const { curators, labels } = separateSponsorsByType(sponsors);

  // Combine curators and labels, prioritizing curators
  const allSponsors = [...curators, ...labels];

  // Determine how many to show
  const sponsorsToShow = allSponsors.slice(0, maxDisplay);
  const remainingCount = allSponsors.length - maxDisplay;

  const renderSponsorLink = (sponsor, index) => (
    <React.Fragment key={`${sponsor.sponsor_id}-${index}`}>
      <Link
        href=""
        to={
          sponsor.sponsor_type === 'artist_meta'
            ? `/artists/${sponsor.sponsor_id}`
            : `/labels/${sponsor.sponsor_id}`
        }
        sx={{
          color: linkColor || '#fff',
          textDecorationColor: linkColor || '#fff',
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          maxWidth: '200px', // Prevent extremely long names from breaking layout
        }}
        underline={underline || 'always'}
      >
        {sponsor.sponsor}
      </Link>
      {index < sponsorsToShow.length - 1 && (
        <Typography
          variant="caption"
          sx={{ color: linkColor || '#fff', whiteSpace: 'nowrap', mx: 0.5 }}
        >
          &
        </Typography>
      )}
    </React.Fragment>
  );

  return (
    <span>
      {sponsorsToShow.map((sponsor, index) =>
        renderSponsorLink(sponsor, index)
      )}
      {showTruncated && remainingCount > 0 && (
        <Typography
          component="span"
          variant="caption"
          sx={{
            color: linkColor || '#fff',
            fontStyle: 'italic',
            whiteSpace: 'nowrap',
            ml: 0.5,
          }}
        >
          +{remainingCount} more
        </Typography>
      )}
    </span>
  );
};

SponsorsName.propTypes = {
  sponsors: PropTypes.array,
  linkColor: PropTypes.string,
  underline: PropTypes.string,
  direction: PropTypes.string,
  maxDisplay: PropTypes.number,
  showTruncated: PropTypes.bool,
};

export default SponsorsName;
